import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../../common';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import { IconButton } from '../../../../common/controls/Controls.jsx';
import { CheckCircleOutlined, UndoOutlined } from '@ant-design/icons';
import { useGetPhdCommentsQuery } from '../../../../../redux/services/phd';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter';

const PhdThesisCommentsNestedTable = ({
  thesisId,
  onEditComment,
  onDeleteComment,
  onToggleResolved,
  canResolveComment,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const updatedByFilter = useDataGridTextFilter('updated_by_name');
  const {
    data: comments,
    isLoading,
    isFetching,
  } = useGetPhdCommentsQuery(
    thesisId ? { phd_thesis_id: thesisId } : undefined,
    { skip: !thesisId },
  );

  const tableData = useMemo(
    () =>
      (comments || []).map((comment) => ({
        ...comment,
        teacher_label:
          comment?.teacher?.full_name ||
          comment?.teacher?.mail ||
          t('Neznámy recenzent'),
      })),
    [comments, t],
  );

  const columns = [
    {
      title: t('Školiteľ / recenzent'),
      dataIndex: 'teacher_label',
      key: 'teacher_label',
      ellipsis: true,
    },
    {
      title: t('Text'),
      dataIndex: 'text',
      key: 'text',
      ellipsis: true,
    },
    {
      title: t('Stav'),
      dataIndex: 'is_resolved',
      key: 'is_resolved',
      width: 140,
      render: (value) => (
        <Tag color={value ? 'green' : 'orange'}>
          {value ? t('Vyriešený') : t('Nevyriešený')}
        </Tag>
      ),
    },
    {
      title: t('Naposledy upravené'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: (value) =>
        value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      title: t('Upravil'),
      dataIndex: 'updated_by_name',
      key: 'updated_by_name',
      ellipsis: true,
      render: (value) => value || '—',
      ...updatedByFilter,
    },
  ];

  if (isLoading || isFetching) {
    return <Spinner />;
  }

  return (
    <DataGrid
      rowKey={(comment) => comment.id}
      columns={columns}
      dataSource={tableData}
      permissionsBasic="phd-comments"
      uniqueTableId={`phd-theses-${thesisId}-comments-table`}
      onEditClick={readOnly ? undefined : onEditComment}
      onRowClick={readOnly ? undefined : onEditComment}
      onDelete={readOnly ? undefined : onDeleteComment}
      secondaryCondition={readOnly ? undefined : (row) => Boolean(row?.is_mine)}
      deleteCondition={readOnly ? undefined : (row) => Boolean(row?.is_mine)}
      customSettings={
        readOnly
          ? undefined
          : (row) => (
              <IconButton
                icon={
                  row?.is_resolved ? <UndoOutlined /> : <CheckCircleOutlined />
                }
                tooltip={
                  row?.is_resolved
                    ? t('Označiť ako nevyriešený')
                    : t('Označiť ako vyriešený')
                }
                disabled={Boolean(canResolveComment && !canResolveComment(row))}
                disabledTooltip={t(
                  'Stav komentára môže meniť len autor alebo administrátor.',
                )}
                onClick={(event) => {
                  event.stopPropagation();
                  if (canResolveComment && !canResolveComment(row)) {
                    return;
                  }
                  onToggleResolved?.(row);
                }}
              />
            )
      }
      editDisabledTooltip={t(
        'Komentár môžete upravovať len ak ste jeho autor.',
      )}
      deleteDisabledTooltip={t(
        'Komentár môžete vymazať len ak ste jeho autor.',
      )}
      disableExpandRow={() => true}
      pageSize={5}
    />
  );
};

PhdThesisCommentsNestedTable.propTypes = {
  thesisId: PropTypes.number,
  onEditComment: PropTypes.func,
  onDeleteComment: PropTypes.func,
  onToggleResolved: PropTypes.func,
  canResolveComment: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default PhdThesisCommentsNestedTable;
